import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import type {
  HNISO4217Alpha3SupportedCurrency,
  ReceiptLineItems,
  ReimbursementTransactionType,
  FlexGlCode,
  AgaveGlCode,
  FlexCode,
  AgaveProject,
  AgaveSubJob,
  FlexCostCode,
  AgaveCostCode,
  FlexCostType,
  AgaveCostType,
  FlexVendor,
  AgaveVendor,
  AgaveLocation,
  AgaveDepartment,
  AgaveSubaccount,
  AgaveEmployeeEntity,
  AgaveEquipment,
  AgaveEmCostCode,
  AgaveEmCostType,
} from 'API';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useIsEnabled } from 'app/shared-components/feature-flags/FeatureFlagProvider';
import { type CodingType } from 'app/util/columnVisibility';
import { createReceiptUpload } from 'app/services/reimbursementService';
import { isNetworkTransportError } from 'app/services/networkTransport';
import { useTranslation } from 'react-i18next';
import { downscaleReceiptImage } from 'app/util/receiptImage';
import { createReimbursement } from 'app/pages/store/reimbursementsSlice';
import { CreateReceiptInput } from 'app/shared-components/receipts/CreateReceiptInput';
import { ReimbursementTransactionInput } from 'app/pages/overview/tabs/reimbursements/submit-reimbursement-sheet/ReimbursementTransactionInput';
import { fileAccessService } from 'app/services/fileAccess.service';
import {
  MAX_RECEIPT_SIZE,
  sanitizeFileName,
  validMimeTypes,
} from 'app/shared-components/receipts/ReceiptConstants';
import { useSnackbar } from 'notistack';
import { captureException } from '@sentry/react';
import { parseCurrencyToCents } from 'app/shared-components/util/Currency';
import { useCodingSystemConfig } from 'app/hooks/transactions/useCodingSystemConfig';
import { isValidMileage } from 'app/pages/overview/tabs/reimbursements/mileage';
import type { ErpPostingAccount } from 'app/services/erp-connection.service';

// ─── Schema ──────────────────────────────────────────────────────────────────

const hasMiles = (v: string | undefined) => !!v && Number(v) > 0;

const schema = yup.object({
  amount: yup
    .string()
    .required('Amount is required')
    .test('validAmount', 'Enter a valid amount greater than $0', (v) => {
      if (!v) return false;
      const clean = v.replace(/[$,]/g, '');
      return /^\d+(\.\d{1,2})?$/.test(clean) && parseFloat(clean) > 0;
    }),
  receiptLineItems: yup.mixed<ReceiptLineItems>().required('Please select a receipt type'),
  transactionMethod: yup.mixed<ReimbursementTransactionType>().required('Please select a payment method'),
  // Mileage reimbursements don't have a merchant, so it's optional then.
  merchantName: yup.string().when('milesDriven', {
    is: hasMiles,
    then: (s) => s.optional(),
    otherwise: (s) => s.required('Merchant name is required'),
  }),
  transactionDate: yup.mixed<Date>().required('Transaction date is required'),
  milesDriven: yup
    .string()
    .optional()
    .test('validMileage', 'Enter a valid mileage', (v) => isValidMileage(v)),
  purposeOfTrip: yup.string().when('milesDriven', {
    is: hasMiles,
    then: (s) => s.required('Purpose of the trip is required for mileage reimbursements'),
    otherwise: (s) => s.optional(),
  }),
  receiptFile: yup
    .mixed<File>()
    .required('Please attach a receipt')
    .test('fileType', 'Invalid file type', (v) => !v || validMimeTypes.includes((v as File).type))
    .test('fileSize', 'File is too large (max 10MB)', (v) => !v || (v as File).size <= MAX_RECEIPT_SIZE),
  notes: yup.string().nullable(),
  glCode: yup.mixed().nullable(),
  project: yup.mixed().nullable(),
  subJob: yup.mixed().nullable(),
  costCode: yup.mixed().nullable(),
  costType: yup.mixed().nullable(),
  vendor: yup.mixed().nullable(),
  location: yup.mixed().nullable(),
  department: yup.mixed().nullable(),
  subaccount: yup.mixed().nullable(),
  employee: yup.mixed().nullable(),
  // SEE-1148 — equipment coding fields (Vista/equipment flag). Registered so
  // RHF setValue actually persists a selection; previously these weren't in the
  // form, so tapping Equipment opened the menu but the pick never stuck.
  equipment: yup.mixed().nullable(),
  emCostCode: yup.mixed().nullable(),
  emCostType: yup.mixed().nullable(),
  codingType: yup.mixed<CodingType>().nullable(),
});

export interface ReimbursementFormValues {
  amount: string;
  receiptLineItems: ReceiptLineItems | null;
  transactionMethod: ReimbursementTransactionType | null;
  merchantName: string;
  transactionDate: Date | null;
  milesDriven: string;
  purposeOfTrip: string;
  receiptFile: File | null;
  notes: string | null;
  glCode: FlexGlCode | AgaveGlCode | null;
  project: FlexCode | AgaveProject | null;
  subJob: AgaveSubJob | null;
  costCode: FlexCostCode | AgaveCostCode | null;
  costType: FlexCostType | AgaveCostType | null;
  vendor: FlexVendor | AgaveVendor | null;
  location: AgaveLocation | null;
  department: AgaveDepartment | null;
  subaccount: AgaveSubaccount | null;
  employee: AgaveEmployeeEntity | null;
  billableItem: ErpPostingAccount | null;
  equipment: AgaveEquipment | null;
  emCostCode: AgaveEmCostCode | null;
  emCostType: AgaveEmCostType | null;
  codingType: CodingType | null;
}

export const DEFAULT_VALUES: ReimbursementFormValues = {
  amount: '',
  receiptLineItems: null,
  transactionMethod: null,
  merchantName: '',
  transactionDate: null,
  milesDriven: '',
  purposeOfTrip: '',
  receiptFile: null,
  notes: '',
  glCode: null,
  project: null,
  subJob: null,
  costCode: null,
  costType: null,
  vendor: null,
  location: null,
  department: null,
  subaccount: null,
  employee: null,
  billableItem: null,
  equipment: null,
  emCostCode: null,
  emCostType: null,
  // Default GL mirrors the web reimbursement sheet's default coding type; only
  // sent to the backend when the equipment flag is on (see onSubmit).
  codingType: 'gl',
};

/**
 * Maps plain form field names to the prefixed transaction field names
 * that getMissingRequiredFields expects.
 */
export function mapFormToTransactionShape(
  values: ReimbursementFormValues,
  isAgaveClient: boolean,
  isFlexAccount: boolean
): Record<string, unknown> {
  if (isAgaveClient) {
    return {
      agaveGlCode: values.glCode,
      agaveProject: values.project,
      agaveCostCode: values.costCode,
      agaveCostType: values.costType,
      agaveVendor: values.vendor,
      agaveSubJob: values.subJob,
      agaveLocation: values.location,
      agaveDepartment: values.department,
      agaveSubaccount: values.subaccount,
      agaveEmployee: values.employee,
      agaveEquipment: values.equipment,
      agaveEmCostCode: values.emCostCode,
      agaveEmCostType: values.emCostType,
    };
  }
  if (isFlexAccount) {
    return {
      flexGlCode: values.glCode,
      flexCode: values.project,
      flexCostCode: values.costCode,
      flexCostType: values.costType,
      flexVendor: values.vendor,
    };
  }
  return {};
}

// ─── Hook ────────────────────────────────────────────────────────────────────

interface UseReimbursementFormProps {
  onClose: () => void;
  onNext: () => void;
}

export const useReimbursementForm = ({ onClose, onNext }: UseReimbursementFormProps) => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { selectedPaidolId, selectedPaidol, paidolUsers } = useAppSelector(selectUserCompanies);
  // Resolve the membership for the SELECTED company, not paidolUsers[0]. With
  // the switcher, the active paidol may not be the user's first membership;
  // pairing selectedPaidolId with the wrong paidolUserId writes the
  // reimbursement under the right company but the wrong user, so it never shows
  // in the submitter's list.
  const paidolUser = paidolUsers?.find(
    (pu) => pu.paidol?.id === selectedPaidolId || pu.paidol_id === selectedPaidolId
  );
  const mileageRate = selectedPaidol?.reimbursementSettings?.mileageRate ?? null;
  const { getMissingRequired, visibleColumnIds, isAgaveClient, isFlexAccount } = useCodingSystemConfig();
  const showCodingStep = visibleColumnIds.length > 0;
  const { 'equipment.enableEquipmentCoding': isEquipmentCodingEnabled } = useIsEnabled([
    { featureName: 'equipment', flagName: 'enableEquipmentCoding' },
  ]);

  const form = useForm<ReimbursementFormValues>({
    resolver: yupResolver(schema),
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });

  const { handleSubmit } = form;

  const uploadReceiptFile = useCallback(
    async (file: File) => {
      if (!selectedPaidolId) return '';
      // SEE-1424: shrink photos before they cross a weak connection.
      const upload = await downscaleReceiptImage(file);
      // SEE-1457: shared sanitizer — the previous local one let ".." through,
      // which the upload handler rejects as path traversal.
      // SEE-1319: the key is chosen server-side under this company's
      // reimbursementReceipts folder; the returned key is what the record stores.
      const result = await fileAccessService.uploadFile({
        kind: 'REIMBURSEMENT_RECEIPT',
        paidolId: selectedPaidolId,
        file: upload,
        fileName: sanitizeFileName(upload.name),
        contentType: upload.type,
      });
      return result.key;
    },
    [selectedPaidolId]
  );

  const onSubmit = handleSubmit(async (data) => {
    if (!selectedPaidolId || !paidolUser) return;

    if (showCodingStep) {
      const missing = getMissingRequired(mapFormToTransactionShape(data, isAgaveClient, isFlexAccount));
      if (missing.length > 0) {
        enqueueSnackbar(`Missing required fields: ${missing.join(', ')}`, { variant: 'warning' });
        return;
      }
    }

    try {
      let receiptId = 'bypass';
      if (data.receiptFile) {
        const filePath = await uploadReceiptFile(data.receiptFile);
        const receiptUploadInput = new CreateReceiptInput(filePath, selectedPaidolId);
        const receiptUpload = await createReceiptUpload(receiptUploadInput);
        receiptId = receiptUpload.id;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const codingPayload = (() => {
        if (isAgaveClient) {
          return {
            agaveGlCodeId: (data.glCode as any)?.id,
            agaveProjectId: (data.project as any)?.id,
            agaveCostCodeId: (data.costCode as any)?.id,
            agaveCostTypeId: (data.costType as any)?.id,
            agaveVendorId: (data.vendor as any)?.id,
            agaveSubJobId: (data.subJob as any)?.id,
            agaveLocationId: (data.location as any)?.id,
            agaveDepartmentId: (data.department as any)?.id,
            agaveSubaccountId: (data.subaccount as any)?.id,
            agaveEmployeeId: (data.employee as any)?.id,
            // SEE-1135 — the billable-item picker stores the whole ErpPostingAccount;
            // split it into the flat id + denormalized name (mirrors the web sheet).
            erpBillableItemId: (data.billableItem as any)?.accountId ?? null,
            erpBillableItemName: (data.billableItem as any)?.accountId
              ? (data.billableItem as any)?.accountName ?? (data.billableItem as any)?.accountId
              : null,
            // Equipment ids + codingType are only sent when the flag is on, so a
            // flag-off submit is byte-for-byte unchanged (mirrors the web
            // reimbursement sheet + the transactions write path).
            ...(isEquipmentCodingEnabled
              ? {
                  agaveEquipmentId: (data.equipment as any)?.id,
                  agaveEmCostCodeId: (data.emCostCode as any)?.id,
                  agaveEmCostTypeId: (data.emCostType as any)?.id,
                  codingType: (data.codingType ?? 'gl').toUpperCase(),
                }
              : {}),
          };
        }
        if (isFlexAccount) {
          return {
            flexGlCodeId: (data.glCode as any)?.id,
            flexCodeId: (data.project as any)?.id,
            flexCostCodeId: (data.costCode as any)?.id,
            flexCostTypeId: (data.costType as any)?.id,
            flexVendorId: (data.vendor as any)?.id,
          };
        }
        return {};
      })();

      const milesNum = Number(data.milesDriven);
      const isMileage =
        mileageRate != null && !!data.milesDriven && isValidMileage(data.milesDriven) && milesNum > 0;

      const reimbursementTransactionInput = new ReimbursementTransactionInput({
        paidolId: selectedPaidolId,
        paidolUserId: paidolUser.id,
        amount: {
          value: parseCurrencyToCents(data.amount),
          currencyCode: 'USD' as HNISO4217Alpha3SupportedCurrency,
        },
        transactionDate: data.transactionDate!.toISOString(),
        note: data.notes || null,
        merchantName: data.merchantName || null,
        receiptLineItems: data.receiptLineItems!,
        transactionType: data.transactionMethod!,
        receiptId: receiptId,
        ...(isMileage
          ? {
              milesDriven: milesNum,
              purposeOfTrip: data.purposeOfTrip || undefined,
              mileageRate,
            }
          : {}),
        ...codingPayload,
      });

      await dispatch(createReimbursement(reimbursementTransactionInput)).unwrap();
      enqueueSnackbar('Reimbursement request created!', { variant: 'success' });
      onNext();
      onClose();
    } catch (err) {
      captureException(err);
      // SEE-1424: a dropped connection is recoverable — say so instead of the generic error.
      enqueueSnackbar(
        isNetworkTransportError(err)
          ? t('connectionDroppedRetry')
          : 'An error occurred while processing your request. Please try again later.',
        { variant: 'error' }
      );
    }
  });

  return {
    form,
    isSubmitting: form.formState.isSubmitting,
    onSubmit,
  };
};
